/**
 * Custom hook for managing network status with background refresh
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector, store } from '../app/store';
import {
  loadFromStorage,
  fetchAllNetworkStatus,
  refreshNetworkStatus,
  selectAutoRefreshEnabled,
  selectIsStale,
} from '../features/networkStatus/networkStatusSlice';
import { networkStatusStorage } from '../services/networkStatusStorage';
import { networkStatusService } from '../services/networkStatusService';
import { ALL_EXCHANGES } from '../types/exchangeEnum';
import type { ExchangeIndex } from '../types/exchangeEnum';

interface UseNetworkStatusOptions {
  refreshInterval?: number; // Default 1 hour (3600000ms)
  enableAutoRefresh?: boolean;
}

export function useNetworkStatus(options: UseNetworkStatusOptions = {}) {
  const {
    refreshInterval = 3600000, // 1 hour default
    enableAutoRefresh = true,
  } = options;

  const dispatch = useAppDispatch();
  const autoRefreshEnabled = useAppSelector(selectAutoRefreshEnabled);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingRefreshRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  // Check if any exchange data is stale
  const isAnyStale = useAppSelector((state) => {
    return ALL_EXCHANGES.some(exchange => selectIsStale(exchange)(state));
  });

  /**
   * Load initial data from storage and check if refresh is needed
   */
  const loadInitialData = useCallback(async () => {
    try {
      // First check if we already have data from WebSocket in Redux store
      const currentData = store.getState().networkStatus?.data;
      if (currentData && Object.keys(currentData).length > 0) {
        console.log('Network status data already present from WebSocket, skipping fetch');
        // Save the WebSocket data to storage for persistence
        await networkStatusStorage.save(currentData);
        return;
      }
      
      // Load from storage if no data in Redux
      const stored = await dispatch(loadFromStorage()).unwrap();
      
      if (!stored || !stored.data || Object.keys(stored.data).length === 0) {
        // No stored data and no WebSocket data - don't try to fetch from API
        // WebSocket will deliver the data shortly
        console.log('No stored network status data, waiting for WebSocket delivery');
        // Note: Removed fetchAllNetworkStatus() as it doesn't work due to CORS
      } else if (networkStatusStorage.isStale(stored.timestamp)) {
        // Stored data is stale - WebSocket will deliver fresh data
        console.log('Stored network status data is stale, waiting for WebSocket update');
        // Note: Removed fetchAllNetworkStatus() as it doesn't work due to CORS
      } else {
        console.log('Using cached network status data');
      }
    } catch (error) {
      console.error('Failed to load initial network status data:', error);
      // Don't try to fetch from API - wait for WebSocket
      console.log('Waiting for WebSocket to deliver network status');
    }
  }, [dispatch]);

  /**
   * Refresh all network status data
   * Note: Since API endpoints don't work due to CORS, this now only
   * logs that refresh is requested. Actual data comes from WebSocket.
   */
  const refreshAll = useCallback(async () => {
    if (pendingRefreshRef.current) {
      console.log('Refresh already pending, skipping');
      return;
    }

    try {
      pendingRefreshRef.current = true;
      console.log('Network status refresh requested - waiting for WebSocket update');
      // Note: Not calling API endpoints as they don't work due to CORS
      // The backend should send updated network status via WebSocket
    } finally {
      pendingRefreshRef.current = false;
    }
  }, []);

  /**
   * Refresh single exchange network status
   * Note: Since API endpoints don't work due to CORS, this now only
   * logs that refresh is requested. Actual data comes from WebSocket.
   */
  const refreshExchange = useCallback(async (exchange: ExchangeIndex) => {
    if (pendingRefreshRef.current) {
      console.log(`Refresh already pending, skipping ${exchange}`);
      return;
    }

    try {
      console.log(`Network status refresh requested for exchange ${exchange} - waiting for WebSocket update`);
      // Note: Not calling API endpoints as they don't work due to CORS
      // The backend should send updated network status via WebSocket
    } catch (error) {
      console.error(`Failed to request network status refresh for ${exchange}:`, error);
    }
  }, []);

  /**
   * Setup auto-refresh timer
   */
  const setupAutoRefresh = useCallback(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only set up interval if auto-refresh is enabled
    if (enableAutoRefresh && autoRefreshEnabled) {
      console.log(`Setting up auto-refresh with ${refreshInterval}ms interval`);
      
      intervalRef.current = setInterval(() => {
        // Check if component is still mounted and document is visible
        if (!mountedRef.current || document.hidden) {
          console.log('Skipping auto-refresh (component unmounted or document hidden)');
          return;
        }

        console.log('Auto-refresh triggered');
        refreshAll();
      }, refreshInterval);
    }
  }, [enableAutoRefresh, autoRefreshEnabled, refreshInterval, refreshAll]);

  /**
   * Handle visibility change - pause refresh when tab is not visible
   */
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      console.log('Document hidden, pausing auto-refresh');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } else {
      console.log('Document visible, resuming auto-refresh');
      setupAutoRefresh();
      
      // Check if data is stale after becoming visible
      if (isAnyStale) {
        console.log('Data is stale after becoming visible, refreshing');
        refreshAll();
      }
    }
  }, [setupAutoRefresh, isAnyStale, refreshAll]);

  // Load initial data on mount
  useEffect(() => {
    mountedRef.current = true;
    loadInitialData();

    return () => {
      mountedRef.current = false;
    };
  }, [loadInitialData]);

  // Setup auto-refresh
  useEffect(() => {
    setupAutoRefresh();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [setupAutoRefresh]);

  // Handle visibility changes
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  return {
    refreshAll,
    refreshExchange,
    isRefreshing: pendingRefreshRef.current,
    isAnyStale,
  };
}